import { SOURCE, INTERNAL_PROTOCOL_VERSION } from './protocol'

// Broadcast (window.postMessage) transport for the FSA-shaped wire
// format. Used for one-way notification-style signals between our
// own same-origin pages (admin / IH page-to-page, our own receivers
// of donationCompleted, etc).
//
// Scope is intentionally same-origin / internal only — that's why
// the wire carries no beta notice and the version stamp is stable.
// Partner-facing traffic goes through publicProtocol.js instead,
// which handles v0 compatibility and the v1 beta marker. If we ever
// need to broadcast cross-origin to a frame we don't control, add a
// partner-aware notice to the envelope before doing so.
//
// The channel transport (channel.js) is the right tool for stateful
// bidirectional protocols. This is the right tool for fire-and-forget
// signals where the receiver doesn't need iframe-scoped routing or
// port lifecycle: a window listener with a structured envelope is
// enough.
//
// Envelope: { type, payload, meta: { source: 'pledge', version: 1, ...} }.

// Send `type` with `payload` to `target` (a Window — typically
// `parent`, an iframe's `contentWindow`, or `window` itself for
// same-window dispatch). Caller can override `meta.version` if the
// message belongs to a different protocol version, or add additional
// meta fields.
export const sendBroadcast = (target, type, payload, meta = {}) =>
  target.postMessage({
    type,
    payload,
    meta: { source: SOURCE, version: INTERNAL_PROTOCOL_VERSION, ...meta },
  }, '*')

// Listen for `type` messages on the current window. Filters by origin
// (defaults to same-origin) and by `meta.source === 'pledge'` so other
// vendors' postMessages on the same window don't trigger our handlers.
// Returns a teardown to remove the listener.
//
// The handler is invoked as `handler(payload, meta, event)`. The third
// arg is the raw MessageEvent — useful when the receiver needs to
// reach for `event.source` (e.g. responding to the originating
// frame) or `event.origin` (when the listener accepts more than one).
export const onBroadcastMessage = (type, handler, { origin = location.origin } = {}) => {
  const listener = e => {
    if (e.origin !== origin) return
    if (e.data?.meta?.source !== SOURCE) return
    if (e.data?.type !== type) return
    handler(e.data.payload, e.data.meta, e)
  }
  addEventListener('message', listener)
  return () => removeEventListener('message', listener)
}
